/**
 * Week date math utilities
 */

/**
 * Get the Monday (start) of the week containing the given date.
 * @param {Date} date
 * @returns {Date} Monday 00:00:00
 */
export function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the Sunday (end) of the week containing the given date.
 * @param {Date} date
 * @returns {Date} Sunday 23:59:59.999
 */
export function getWeekEnd(date) {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Shift a date by N weeks.
 * @param {Date} date
 * @param {number} weeks - positive = forward, negative = backward
 * @returns {Date}
 */
export function shiftWeek(date, weeks) {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a week range for display.
 * @param {Date} start
 * @param {Date} end
 * @returns {string} e.g. "Mon 24 Feb - Sun 2 Mar 2026"
 */
export function formatWeekRange(start, end) {
  const s = `${DAY_NAMES[start.getDay()]} ${start.getDate()} ${MONTH_NAMES[start.getMonth()]}`;
  const e = `${DAY_NAMES[end.getDay()]} ${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
  return `${s} - ${e}`;
}

/**
 * Format a week range for file naming.
 * @param {Date} start
 * @param {Date} end
 * @returns {string} e.g. "Mon_24_Feb-Sun_2_Mar_2026"
 */
export function formatWeekRangeForFile(start, end) {
  const s = `${DAY_NAMES[start.getDay()]}_${start.getDate()}_${MONTH_NAMES[start.getMonth()]}`;
  const e = `${DAY_NAMES[end.getDay()]}_${end.getDate()}_${MONTH_NAMES[end.getMonth()]}_${end.getFullYear()}`;
  return `${s}-${e}`;
}

/**
 * Get the month start/end that contains the given date.
 * Useful for budget review (monthly scope).
 * @param {Date} date
 * @returns {{ monthStart: Date, monthEnd: Date }}
 */
export function getMonthBounds(date) {
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
  return { monthStart, monthEnd };
}

/**
 * Convert a Firestore timestamp or string to a JS Date.
 * @param {any} val - Firestore Timestamp, Date, or date string
 * @returns {Date|null}
 */
export function toDate(val) {
  if (!val) return null;
  // Firestore Timestamp: has toDate method and seconds field
  if (typeof val.toDate === 'function' && val.seconds !== undefined) return val.toDate();
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Check if a date falls within [start, end] inclusive.
 * @param {Date} date
 * @param {Date} start
 * @param {Date} end
 * @returns {boolean}
 */
export function isWithinRange(date, start, end) {
  if (!date) return false;
  return date >= start && date <= end;
}
